
import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import FichajeForm from './components/FichajeForm';
import PanelAdmin from './components/PanelAdmin';
import LegalInfo from './components/LegalInfo';
import MisFichajes from './components/MisFichajes';
import Login from './components/Login';
import UsuarioInfo from './components/UsuarioInfo';
import PanelModificacionFichajes from './components/PanelModificacionFichajes';
import { API_BASE } from './api';

const theme = createTheme();

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [role, setRole] = useState(() => localStorage.getItem('role'));
  const [usuario, setUsuario] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Validar token y obtener usuario al cargar la app
  React.useEffect(() => {
    async function validarToken() {
      if (token) {
        try {
          const apiUrl = `${API_BASE}/api/auth/me`;
          const res = await fetch(apiUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) throw new Error('Token inválido');
          const userData = await res.json();
          setUsuario(userData);
        } catch {
          setToken(null);
          setRole(null);
          setUsuario(null);
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          window.location.href = '/';
        }
      }
    }
    validarToken();
  }, [token]);

  const handleLogin = (token, role) => {
    setToken(token);
    setRole(role);
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);
  };

  const handleLogout = () => {
    setToken(null);
    setRole(null);
    setUsuario(null);
    localStorage.clear();
    window.location.href = '/';
  };
  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <div className="app-container">
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-10px) rotate(3deg); }
            }
            
            @keyframes gradientShift {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.05); }
            }
            
            .logout-btn {
              position: absolute;
              top: 20px;
              right: 20px;
              padding: 12px 24px;
              border: 3px solid transparent;
              background: 
                linear-gradient(white, white) padding-box,
                linear-gradient(135deg, #FF3B8F 0%, #A855F7 20%, #3B82F6 40%, #06B6D4 60%, #10B981 80%, #FB923C 100%) border-box;
              background-size: auto, 300% 300%;
              color: #1e293b;
              border-radius: 12px;
              font-weight: 800;
              font-size: 0.95rem;
              cursor: pointer;
              transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
              backdrop-filter: blur(10px);
              z-index: 10;
              box-shadow: 
                0 4px 14px rgba(0, 0, 0, 0.08),
                0 2px 8px rgba(0, 0, 0, 0.05);
              overflow: visible;
              animation: logoutRainbow 8s linear infinite;
            }
            
            @keyframes logoutRainbow {
              0% { background-position: 0% 50%, 0% 50%; }
              50% { background-position: 0% 50%, 100% 50%; }
              100% { background-position: 0% 50%, 0% 50%; }
            }
            
            @keyframes redOrb {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
              50% { transform: translate(15px, -8px) scale(1.2); opacity: 0.7; }
            }
            
            .logout-btn::before {
              content: '';
              position: absolute;
              width: 55px;
              height: 55px;
              background: radial-gradient(circle, rgba(255, 59, 143, 0.15) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 70%);
              border-radius: 50%;
              top: 50%;
              left: -10px;
              transform: translateY(-50%);
              animation: redOrb 6s ease-in-out infinite;
              filter: blur(20px);
              pointer-events: none;
              z-index: -1;
            }
            
            .logout-btn::after {
              content: '';
              position: absolute;
              width: 45px;
              height: 45px;
              background: radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(16, 185, 129, 0.1) 50%, transparent 70%);
              border-radius: 50%;
              top: 50%;
              right: -10px;
              transform: translateY(-50%);
              animation: redOrb 8s ease-in-out infinite reverse;
              filter: blur(22px);
              pointer-events: none;
              z-index: -1;
            }
            
            .logout-btn:hover {
              transform: translateY(-3px) scale(1.05);
              box-shadow: 
                0 8px 24px rgba(0, 0, 0, 0.12),
                0 4px 12px rgba(0, 0, 0, 0.08),
                0 0 40px rgba(168, 85, 247, 0.15);
              animation: logoutRainbow 4s linear infinite;
            }
            
            .logout-btn:active {
              transform: translateY(-1px) scale(1.02);
              box-shadow: 
                0 4px 12px rgba(0, 0, 0, 0.1),
                0 2px 6px rgba(0, 0, 0, 0.06);
            }
            
            .header-container {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 40px;
              padding: 28px;
              background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 50%, rgba(102, 126, 234, 0.08) 100%);
              background-size: 200% 200%;
              border-radius: 20px;
              border: 2px solid rgba(102, 126, 234, 0.2);
              animation: fadeIn 0.8s ease-out, gradientShift 6s ease infinite;
              position: relative;
              overflow: hidden;
            }
            
            .header-container::before {
              content: '';
              position: absolute;
              top: -50%;
              left: -50%;
              width: 200%;
              height: 200%;
              background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
              animation: pulse 4s ease-in-out infinite;
            }
            
            .app-logo {
              height: 70px;
              width: auto;
              margin-right: 24px;
              border-radius: 16px;
              box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
              background: white;
              padding: 10px;
              transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
              animation: float 3s ease-in-out infinite;
              position: relative;
              z-index: 1;
            }
            
            .app-logo:hover {
              transform: scale(1.1) rotate(8deg);
              box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4);
            }
            
            .app-title {
              font-family: 'Poppins', sans-serif;
              font-weight: 800;
              fontSize: 2.2rem;
              margin: 0;
              letter-spacing: -0.03em;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%);
              background-size: 200% 200%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: gradientShift 4s ease infinite;
              position: relative;
              z-index: 1;
              filter: drop-shadow(0 2px 4px rgba(102, 126, 234, 0.2));
            }
            
            @media (max-width: 600px) {
              .app-logo {
                height: 52px;
                margin-right: 14px;
              }
              .app-title {
                font-size: 1.6rem;
              }
              .logout-btn {
                padding: 10px 18px;
                font-size: 0.85rem;
              }
            }
          `}</style>
          
          <div className="header-container">
            <img
              src={process.env.REACT_APP_ICON_PATH || '/icono-app.png'}
              alt="App Icon"
              className="app-logo"
              draggable={false}
            />
            <h1 className="app-title">
              {process.env.REACT_APP_CLINIC_NAME || 'Sistema de Fichaje'}
            </h1>
          </div>
          
          <button className="logout-btn" onClick={handleLogout}>
            <span style={{ marginRight: '6px' }}>🚪</span>
            Cerrar sesión
          </button>
          
          {role !== 'admin' && (
            <UsuarioInfo token={token} />
          )}
          
          <Routes>
            <Route path="/admin" element={role === 'admin' ? (
              <>
                <FichajeForm token={token} onFichajeRealizado={() => setRefreshKey(k => k + 1)} />
                <MisFichajes token={token} usuarioId={usuario?._id} refreshKey={refreshKey} />
                <PanelAdmin token={token} style={{maxWidth:480, margin:'1em auto'}} />
              </>
            ) : <Navigate to="/usuario" />} />
            <Route path="/usuario" element={role === 'admin' ? (
              <Navigate to="/admin" />
            ) : (
              <>
                <FichajeForm token={token} onFichajeRealizado={() => setRefreshKey(k => k + 1)} />
                <MisFichajes token={token} usuarioId={usuario?._id} refreshKey={refreshKey} />
                {/* El panel de modificación de fichajes se abrirá desde UsuarioInfo.js mediante un modal */}
                <LegalInfo style={{fontSize:'0.95em', margin:'0.5em auto'}} />
              </>
            )} />
            <Route path="/" element={<Navigate to={role === 'admin' ? '/admin' : '/usuario'} />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
